import { addDoc, collection, deleteDoc, doc, getDocs, query, Timestamp, updateDoc, where } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import type { Questionnaire } from '@/types/questionnaire';
import {
  ageInYears,
  resolveAssignment,
  ruleProblem,
  type AssignmentRule,
} from '@/lib/assessment/questionnaireAssignment';
import { getDefaultQuestionnaire, getQuestionnaire, getQuestionnaires } from './questionnaires';
import { getPatientDemographics } from './patientDemographics';

// The admin's questionnaire assignment rules (#92), one doc per rule in
// `questionnaireRules`: { organizationId|null, ageFrom|null, ageTo|null,
// questionnaireId, createdAt }. Choosing is done by the pure resolver.

const RULES = 'questionnaireRules';

export async function getAssignmentRules(): Promise<AssignmentRule[]> {
  const snap = await getDocs(collection(db, RULES));
  return snap.docs
    .map((d) => {
      const data = d.data();
      const rule: AssignmentRule = {
        id: d.id,
        organizationId: data.organizationId ?? null,
        ageFrom: typeof data.ageFrom === 'number' ? data.ageFrom : null,
        ageTo: typeof data.ageTo === 'number' ? data.ageTo : null,
        questionnaireId: data.questionnaireId as string,
      };
      return { rule, at: (data.createdAt?.toMillis?.() as number | undefined) ?? 0 };
    })
    .sort((a, b) => a.at - b.at)
    .map((x) => x.rule);
}

export class RuleRefusedError extends Error {
  constructor(public problem: 'empty_range' | 'overlap') {
    super(problem);
  }
}

/** Adds (no id) or updates a rule after checking it against the others. */
export async function saveAssignmentRule(rule: Omit<AssignmentRule, 'id'> & { id?: string }): Promise<void> {
  const existing = await getAssignmentRules();
  const problem = ruleProblem({ ...rule, id: rule.id ?? '' }, existing);
  if (problem) throw new RuleRefusedError(problem);
  const data = {
    organizationId: rule.organizationId || null,
    ageFrom: rule.ageFrom ?? null,
    ageTo: rule.ageTo ?? null,
    questionnaireId: rule.questionnaireId,
    updatedAt: Timestamp.now(),
  };
  if (rule.id) await updateDoc(doc(db, RULES, rule.id), data);
  else await addDoc(collection(db, RULES), { ...data, createdAt: Timestamp.now() });
}

export async function deleteAssignmentRule(id: string): Promise<void> {
  await deleteDoc(doc(db, RULES, id));
}

async function organizationIdsOf(userId: string): Promise<string[]> {
  const snap = await getDocs(query(collection(db, 'organizationMembers'), where('userId', '==', userId)));
  return [...new Set(snap.docs.map((d) => d.data().organizationId as string).filter(Boolean))];
}

/**
 * The questionnaire this beneficiary should fill now. Falls back to the old
 * `isDefault` questionnaire only while no rule exists (before the rules are
 * seeded).
 */
export async function getAssignedQuestionnaire(userId: string, now: Date = new Date()): Promise<Questionnaire | null> {
  const [rules, questionnaires, organizationIds, demographics] = await Promise.all([
    getAssignmentRules(),
    getQuestionnaires(),
    organizationIdsOf(userId),
    getPatientDemographics(userId).catch(() => ({ dateOfBirth: null })),
  ]);
  if (rules.length === 0) return getDefaultQuestionnaire();
  const archived = questionnaires.filter((q) => q.archived).map((q) => q.id);
  const rule = resolveAssignment(rules, { organizationIds, age: ageInYears(demographics.dateOfBirth, now) }, archived);
  if (!rule) return null;
  return questionnaires.find((q) => q.id === rule.questionnaireId) ?? getQuestionnaire(rule.questionnaireId);
}
